import * as I from 'immutable';

import {AlertPolicy, ApiFeature} from 'app/modules/Remote/Feature';
import {
  BaseAlertPropertyConfiguration,
  CompositeAlertProperties,
  OverlaySelection,
  RangeCondition,
} from 'app/modules/Remote/Feature/types';
import * as CONSTANTS from 'app/utils/constants';
import * as featureUtils from 'app/utils/featureUtils';
import * as layerUtils from 'app/utils/layerUtils';

export const isDefaultUpstreamVegetationDropAlertPolicy = (alertPolicy: AlertPolicy) =>
  alertPolicy.name === CONSTANTS.ALERT_VEGETATION_DROP_DEFAULT_POLICY_NAME;

// An overlay on a policy that is neither on the portfolio nor in the organization's library, e.g.
// one Upstream manages for the organization. The app can't fetch another organization's feature
// collection to name it.
export const MANAGED_OVERLAY_LABEL = 'Managed by Lens (not in this portfolio)';
export const NOT_ON_PORTFOLIO_NOTE = 'not in this portfolio';

export const overlayLabel = (
  featureCollectionId: number,
  projectOverlays: Array<{id: number; name: string}>,
  organizationOverlays: Array<{id: number; name: string}>
): string => {
  const onPortfolio = projectOverlays.find((overlay) => overlay.id === featureCollectionId);
  if (onPortfolio) return onPortfolio.name;
  const inLibrary = organizationOverlays.find((overlay) => overlay.id === featureCollectionId);
  return inLibrary ? `${inLibrary.name} (${NOT_ON_PORTFOLIO_NOTE})` : MANAGED_OVERLAY_LABEL;
};

/**
 * A policy's overlays as the app works with them. The API carries a policy's single overlay beside
 * the lists and counts it in the list its method names; merging it in here, and clearing its own
 * keys, means the form shows every overlay in one place and a save writes them all through the
 * lists.
 */
export function normalizeOverlaySelections<T extends BaseAlertPropertyConfiguration>(
  properties: T
): T {
  const {overlayFeatureCollectionId, overlayMethod} = properties;
  const includeOverlays = properties.includeOverlays ?? [];
  const excludeOverlays = properties.excludeOverlays ?? [];
  if (overlayFeatureCollectionId == null) {
    return {...properties, includeOverlays, excludeOverlays};
  }
  const single: OverlaySelection = {featureCollectionId: overlayFeatureCollectionId, filter: null};
  const isInclude = overlayMethod === 'intersection';
  return {
    ...properties,
    overlayFeatureCollectionId: null,
    overlayMethod: null,
    includeOverlays: isInclude ? [single, ...includeOverlays] : includeOverlays,
    excludeOverlays: isInclude ? excludeOverlays : [single, ...excludeOverlays],
  };
}

export const normalizeAlertPolicy = (alertPolicy: AlertPolicy): AlertPolicy =>
  ({...alertPolicy, properties: normalizeOverlaySelections(alertPolicy.properties)}) as AlertPolicy;

export const alertPolicyUsesOverlay = (alertPolicy: AlertPolicy, featureCollectionId: number) =>
  [...alertPolicy.properties.includeOverlays, ...alertPolicy.properties.excludeOverlays].some(
    (selection) => selection.featureCollectionId === featureCollectionId
  );

/** "Wetlands (WETLAND_TYPE: Lake, Riverine), Managed by Lens (not in this portfolio)" */
export const formatOverlaySelections = (
  selections: OverlaySelection[],
  getOverlayLabel: (featureCollectionId: number) => string
) =>
  selections
    .map((selection) => {
      const name = getOverlayLabel(selection.featureCollectionId);
      const {filter} = selection;
      return filter ? `${name} (${filter.attribute}: ${filter.values.join(', ')})` : name;
    })
    .join(', ');

const isEnrolled = (alertPolicy: AlertPolicy, featureId: number | string) =>
  !!alertPolicy.enrollments?.some((enrollment) => enrollment?.featureId === featureId);

// Lookouts that stop monitoring the given features when they leave this portfolio. The default
// veg-drop lookout is re-enrolled at the destination unless the destination is a new portfolio.
export const lookoutsStoppedByMove = (
  alertPolicies: AlertPolicy[],
  featureIds: number[],
  includeDefaultVegetationDrop = false
): AlertPolicy[] =>
  alertPolicies.filter(
    (policy) =>
      !policy.isArchived &&
      (includeDefaultVegetationDrop || !isDefaultUpstreamVegetationDropAlertPolicy(policy)) &&
      featureIds.some((featureId) => isEnrolled(policy, featureId))
  );

export const countEnabledLookouts = (
  feature: I.ImmutableOf<ApiFeature>,
  alertPolicies: AlertPolicy[]
): number => {
  const featureId = feature.get('id');
  const customPolicyCount = alertPolicies.filter(
    (alertPolicy) =>
      !isDefaultUpstreamVegetationDropAlertPolicy(alertPolicy) && isEnrolled(alertPolicy, featureId)
  ).length;
  const builtInCount = [
    alertPolicies.some(
      (alertPolicy) =>
        isDefaultUpstreamVegetationDropAlertPolicy(alertPolicy) &&
        isEnrolled(alertPolicy, featureId)
    ),
    featureUtils.keyInAppProperties(feature, CONSTANTS.ALERT_OWNERSHIP_CHANGE_ENROLLMENT_KEY),
    featureUtils.keyInAppProperties(feature, CONSTANTS.ALERT_PARCEL_BOUNDARY_CHANGE_ENROLLMENT_KEY),
  ].filter(Boolean).length;
  return customPolicyCount + builtInCount;
};

export const formatCondition = (sourceId: string, condition: RangeCondition) => {
  const layerKey = layerUtils.getLayerKeyFromSourceAndLayer(sourceId, condition.layerId);
  // shortName isn't unique within a source — SALO publishes the same product at 30m and 3m — and
  // display is what the dataset picker labelled the layer with.
  const name = layerUtils.getLayer(layerKey).display;
  // Bounds are inclusive unless a policy says otherwise, which is what a converted threshold does.
  const minOp = condition.minInclusive === false ? '>' : '≥';
  const maxOp = condition.maxInclusive === false ? '<' : '≤';

  if (condition.min !== null && condition.max !== null) {
    if (condition.min === condition.max) return `${name} = ${condition.min}`;
    // An en dash can't carry which end is open, so a strict bound falls back to comparisons.
    if (minOp === '≥' && maxOp === '≤') return `${name} ${condition.min}–${condition.max}`;
    return `${name} ${minOp} ${condition.min} and ${maxOp} ${condition.max}`;
  }
  if (condition.min !== null) return `${name} ${minOp} ${condition.min}`;
  if (condition.max !== null) return `${name} ${maxOp} ${condition.max}`;
  // Both bounds cleared, which the builder allows mid-edit and blocks on Next.
  return name;
};

/**
 * A composite policy's rule as one line: "(Vegetation 0.3–1 AND Surface Water ≥ 0.1) OR (Built-up ≥ 0.5)".
 *
 * Every group is parenthesized whenever there is more than one group, even a single-condition one,
 * so the group structure is visible at a glance. Without that, "A AND B OR C" is ambiguous wherever
 * markup is stripped, and markup alone can't disambiguate it in an email or a CSV.
 */
export const formatCompositeExpression = (
  sourceId: string,
  properties: CompositeAlertProperties
) => {
  const {groups} = properties;
  return groups
    .map((group) => {
      const text = group.map((condition) => formatCondition(sourceId, condition)).join(' AND ');
      return groups.length > 1 ? `(${text})` : text;
    })
    .join(' OR ');
};

/**
 * A composite policy's rule cut to fit a character budget, for a table cell — e.g. a portfolio
 * table column.
 *
 * Cuts only between whole conditions (never inside one, so a bound like "0.35–0.9" can't come out
 * as "0.3"), and drops the AND/OR grouping parens rather than leave one unclosed. Callers should
 * still surface the full text (tooltip or `title`) since this is lossy.
 */
export const formatCompositeExpressionTruncated = (
  sourceId: string,
  properties: CompositeAlertProperties,
  maxLength: number
) => {
  const full = formatCompositeExpression(sourceId, properties);
  if (full.length <= maxLength) return full;

  const ELLIPSIS = '…';
  const budget = maxLength - ELLIPSIS.length - 1; // room for the trailing space + ellipsis

  const tokens: string[] = [];
  properties.groups.forEach((group, groupIndex) => {
    group.forEach((condition, conditionIndex) => {
      if (groupIndex > 0 && conditionIndex === 0) tokens.push('OR');
      else if (conditionIndex > 0) tokens.push('AND');
      tokens.push(formatCondition(sourceId, condition));
    });
  });

  let result = '';
  for (const token of tokens) {
    const candidate = result ? `${result} ${token}` : token;
    if (candidate.length > budget && result) break;
    result = candidate;
    if (candidate.length > budget) break; // first token alone already over budget
  }
  // Never leave a dangling joiner.
  result = result.replace(/\s+(AND|OR)$/, '');

  return `${result} ${ELLIPSIS}`;
};
